import React, { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SelectProps } from 'antd';
import _get from 'lodash/get';
import { Values } from '@lib/interfaces/form';
import { AccessComponentEnum, AccessLevelEnum, Team } from 'graphql-common';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import Typography from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import FormItem from '@lib/components/ReactHookForm/FormItem';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import InterventionCategoriesChipsListField from '@lib/components/InterventionCategoryChipListField/InterventionCategoryChipListField';
import AssetCategoriesChipsListField from '@lib/components/AssetCategoryChipListField/AssetCategoryChipListField';
import DocumentationCategoriesChipsListField from '@lib/components/DocumentationCategoryChipListField/DocumentationCategoryChipListField';
import Select from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import {
  AccessesFieldType,
  getAccessCategoriesScopeIds,
  getAccessesManagementOptions,
  getAccessLevelOptions,
  getAccessLevelShortOptions,
} from '@lib/utils/accessesManagement';
import { SingleQueryHookResult } from 'utils/fetch/types';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { APP_URLS } from 'constants/urls';
import getAccessListItemContentClassName from 'utils/getAccessListItemContentClassName';
import styles from './TeamAccess.module.scss';

type Props = {
  name: string;
  getAssetCategoriesLoadOptions: GetLoadOptions;
  getDocumentationCategoriesLoadOptions: GetLoadOptions;
  getInterventionCategoriesLoadOptions: GetLoadOptions;
  useTeamQueryHookResult: SingleQueryHookResult<Team>;
  values: Values;
};

const TeamAccessField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const {
      name,
      getAssetCategoriesLoadOptions,
      getDocumentationCategoriesLoadOptions,
      getInterventionCategoriesLoadOptions,
      useTeamQueryHookResult,
      values,
    } = props;
    const { t } = useTranslation();
    const { getUserAccessByCompany, isFullAdmin }: ContextProps =
      useOutletContext();
    const isInterventionsLocked =
      getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
      AccessLevelEnum.LimitedRead;
    const isDocumentationLocked =
      getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;
    // AI assistance is a company-plan-level module: hide the per-user/team
    // setting entirely when the company plan doesn't grant it, instead of
    // showing a toggle that can never take effect.
    const isAiAssistanceHidden =
      getUserAccessByCompany(AccessComponentEnum.AiAssistance) !==
      AccessLevelEnum.Write;

    // Intervention categories ids
    const interventionCategoriesIds = getAccessCategoriesScopeIds(
      useTeamQueryHookResult.data?.accesses,
      AccessComponentEnum.InterventionsManagementCategoriesScope,
    );
    // Asset categories ids
    const assetCategoriesIds = getAccessCategoriesScopeIds(
      useTeamQueryHookResult.data?.accesses,
      AccessComponentEnum.AssetsManagementCategoriesScope,
    );
    // Documentation categories ids
    const documentationCategoriesIds = getAccessCategoriesScopeIds(
      useTeamQueryHookResult.data?.accesses,
      AccessComponentEnum.DocumentationManagementCategoriesScope,
    );

    return (
      <div>
        {getAccessesManagementOptions().map((option) => {
          if (
            option.value === AccessComponentEnum.AiAssistance &&
            isAiAssistanceHidden
          ) {
            return null;
          }
          const levelOptions =
            option.fieldType === AccessesFieldType.ShortOptions
              ? getAccessLevelShortOptions()
              : getAccessLevelOptions();
          return (
            <div key={option.value} className={styles.accessListItem}>
              <div
                className={getAccessListItemContentClassName(
                  option.fieldType,
                  styles,
                  option.value ===
                    AccessComponentEnum.InterventionsManagement &&
                    isInterventionsLocked,
                )}
              >
                <div className={styles.accessListItemTitleWrap}>
                  <div className={styles.accessListItemTitle}>
                    <Typography variant="body" strong>
                      {t(option.labelTransKey)}
                    </Typography>
                    {!!option.descriptionTransKey && (
                      <div className={styles.accessListItemTitleInfo}>
                        <Tooltip body={t(option.descriptionTransKey)}>
                          <MaterialIcon
                            cursor="help"
                            icon="info"
                            symbolsOutlined
                            size="md-16"
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.col}>
                  <div className={styles.accessListItemField}>
                    {option.value ===
                      AccessComponentEnum.InterventionsManagement &&
                    isInterventionsLocked ? (
                      <Tooltip body={t('lock-state-title-interventions')}>
                        <Chip
                          leftIcon="lock"
                          size={ChipSizes.small}
                          type={ChipTypes.chipBlue}
                          to={APP_URLS.dashboard.interventions.index.path}
                          asBlockElement
                        />
                      </Tooltip>
                    ) : (
                      <FormItem
                        name={`${name}.${option.value}.level`}
                        element={RadioButtonGroup}
                        componentProps={{
                          options: levelOptions,
                          RadioButtonComponent: RadioButtonAsButton,
                          wrapView: RadioButtonWrapViews.rowNoWrap,
                        }}
                        hideErrorsSpace
                        formItemClassName={styles.accessListItemFieldEmpty}
                      />
                    )}
                  </div>
                </div>
              </div>
              {option.value === AccessComponentEnum.InterventionsManagement &&
              isInterventionsLocked
                ? null
                : !!option.subOptions?.length &&
                  option.subOptions.map((subOption) => {
                    const isDocumentationSubOptionLocked =
                      option.value ===
                        AccessComponentEnum.DocumentationManagement &&
                      isDocumentationLocked;
                    if (isDocumentationSubOptionLocked && !isFullAdmin)
                      return null;
                    const isParentRestricted =
                      _get(values, `${name}.${option.value}.level.value`) ===
                      AccessLevelEnum.LimitedRead;
                    const isCategoriesSelectSubOption =
                      subOption.fieldType ===
                        AccessesFieldType.InterventionCategoriesSelect ||
                      subOption.fieldType ===
                        AccessesFieldType.AssetCategoriesSelect ||
                      subOption.fieldType ===
                        AccessesFieldType.DocumentationCategoriesSelect;
                    // Hide the "Provide selective access to categories" row
                    // entirely when the parent module is set to Restricted.
                    if (isParentRestricted && isCategoriesSelectSubOption)
                      return null;
                    return (
                      <div
                        key={subOption.value}
                        className={getAccessListItemContentClassName(
                          subOption.fieldType,
                          styles,
                          isDocumentationSubOptionLocked,
                        )}
                      >
                        <div className={styles.accessListItemTitleWrap}>
                          <div className={styles.accessListItemTitle}>
                            <Typography variant="body">
                              {t(subOption.labelTransKey)}
                            </Typography>
                            {!!subOption.descriptionTransKey && (
                              <div className={styles.accessListItemTitleInfo}>
                                <Tooltip
                                  body={t(subOption.descriptionTransKey)}
                                >
                                  <MaterialIcon
                                    cursor="help"
                                    icon="info"
                                    symbolsOutlined
                                    size="md-16"
                                  />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </div>
                        {isDocumentationSubOptionLocked && (
                          <div className={styles.col}>
                            <div className={styles.accessListItemField}>
                              <Tooltip
                                body={t('lock-state-title-documentation')}
                              >
                                <Chip
                                  leftIcon="lock"
                                  size={ChipSizes.small}
                                  type={ChipTypes.chipBlue}
                                  to={
                                    APP_URLS.dashboard.documentation.index.path
                                  }
                                  asBlockElement
                                />
                              </Tooltip>
                            </div>
                          </div>
                        )}
                        {!isDocumentationSubOptionLocked &&
                          subOption.fieldType ===
                            AccessesFieldType.FullOptions && (
                            <div className={styles.col}>
                              <div className={styles.accessListItemField}>
                                <FormItem
                                  name={`${name}.${subOption.value}.level`}
                                  element={RadioButtonGroup}
                                  componentProps={{
                                    options: getAccessLevelOptions(),
                                    RadioButtonComponent: RadioButtonAsButton,
                                    wrapView: RadioButtonWrapViews.rowNoWrap,
                                  }}
                                  hideErrorsSpace
                                  formItemClassName={
                                    styles.accessListItemFieldEmpty
                                  }
                                />
                              </div>
                            </div>
                          )}
                        {!isDocumentationSubOptionLocked &&
                          subOption.fieldType ===
                            AccessesFieldType.ShortOptions && (
                            <div className={styles.col}>
                              <div
                                className={classNames(
                                  styles.accessListItemField,
                                  styles.row,
                                )}
                              >
                                <FormItem
                                  name={`${name}.${subOption.value}.level`}
                                  element={RadioButtonGroup}
                                  componentProps={{
                                    options: getAccessLevelShortOptions(),
                                    RadioButtonComponent: RadioButtonAsButton,
                                    wrapView: RadioButtonWrapViews.rowNoWrap,
                                  }}
                                  hideErrorsSpace
                                  formItemClassName={
                                    styles.accessListItemFieldEmpty
                                  }
                                />
                              </div>
                            </div>
                          )}
                        {!isDocumentationSubOptionLocked &&
                          subOption.fieldType ===
                            AccessesFieldType.InterventionCategoriesSelect && (
                            <div className={styles.col}>
                              <div
                                className={classNames(
                                  styles.accessListItemField,
                                  styles.accessListItemFieldWithChipList,
                                  styles.row,
                                )}
                              >
                                <FormItem
                                  name={`${name}.${subOption.value}.items`}
                                  element={Select}
                                  label={t('intervention-categories')}
                                  componentProps={
                                    {
                                      getLoadOptions:
                                        getInterventionCategoriesLoadOptions,
                                      isAsync: true,
                                      isMulti: true,
                                      defaultOptions: false,
                                      components: {
                                        ValueContainer:
                                          MultiSelectValueContainer,
                                      },
                                    } as SelectProps
                                  }
                                  hideErrorsSpace
                                  isUseWatch
                                  formItemClassName={
                                    styles.accessListItemFieldEmpty
                                  }
                                />
                                <div
                                  style={
                                    _get(
                                      values,
                                      `${name}.${subOption.value}.items.length`,
                                    )
                                      ? undefined
                                      : { display: 'none' }
                                  }
                                >
                                  <FormItem
                                    name={`${name}.${subOption.value}.itemsList`}
                                    element={
                                      InterventionCategoriesChipsListField
                                    }
                                    hideErrorsSpace
                                    isUseWatch
                                    componentProps={{
                                      fieldName: `${name}.${subOption.value}.items`,
                                      ids: interventionCategoriesIds,
                                    }}
                                    formItemClassName={
                                      styles.accessListItemFieldEmpty
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        {!isDocumentationSubOptionLocked &&
                          subOption.fieldType ===
                            AccessesFieldType.AssetCategoriesSelect && (
                            <div className={styles.col}>
                              <div
                                className={classNames(
                                  styles.accessListItemField,
                                  styles.accessListItemFieldWithChipList,
                                  styles.row,
                                )}
                              >
                                <FormItem
                                  name={`${name}.${subOption.value}.items`}
                                  element={Select}
                                  label={t('asset-categories')}
                                  componentProps={
                                    {
                                      getLoadOptions:
                                        getAssetCategoriesLoadOptions,
                                      isAsync: true,
                                      isMulti: true,
                                      defaultOptions: false,
                                      components: {
                                        ValueContainer:
                                          MultiSelectValueContainer,
                                      },
                                    } as SelectProps
                                  }
                                  hideErrorsSpace
                                  isUseWatch
                                  formItemClassName={
                                    styles.accessListItemFieldEmpty
                                  }
                                />
                                <div
                                  style={
                                    _get(
                                      values,
                                      `${name}.${subOption.value}.items.length`,
                                    )
                                      ? undefined
                                      : { display: 'none' }
                                  }
                                >
                                  <FormItem
                                    name={`${name}.${subOption.value}.itemsList`}
                                    element={AssetCategoriesChipsListField}
                                    hideErrorsSpace
                                    isUseWatch
                                    componentProps={{
                                      fieldName: `${name}.${subOption.value}.items`,
                                      ids: assetCategoriesIds,
                                    }}
                                    formItemClassName={
                                      styles.accessListItemFieldEmpty
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        {!isDocumentationSubOptionLocked &&
                          subOption.fieldType ===
                            AccessesFieldType.DocumentationCategoriesSelect && (
                            <div className={styles.col}>
                              <div
                                className={classNames(
                                  styles.accessListItemField,
                                  styles.accessListItemFieldWithChipList,
                                  styles.row,
                                )}
                              >
                                <FormItem
                                  name={`${name}.${subOption.value}.items`}
                                  element={Select}
                                  label={t('document-categories')}
                                  componentProps={
                                    {
                                      getLoadOptions:
                                        getDocumentationCategoriesLoadOptions,
                                      isAsync: true,
                                      isMulti: true,
                                      defaultOptions: false,
                                      components: {
                                        ValueContainer:
                                          MultiSelectValueContainer,
                                      },
                                    } as SelectProps
                                  }
                                  hideErrorsSpace
                                  isUseWatch
                                  formItemClassName={
                                    styles.accessListItemFieldEmpty
                                  }
                                />
                                <div
                                  style={
                                    _get(
                                      values,
                                      `${name}.${subOption.value}.items.length`,
                                    )
                                      ? undefined
                                      : { display: 'none' }
                                  }
                                >
                                  <FormItem
                                    name={`${name}.${subOption.value}.itemsList`}
                                    element={
                                      DocumentationCategoriesChipsListField
                                    }
                                    hideErrorsSpace
                                    isUseWatch
                                    componentProps={{
                                      fieldName: `${name}.${subOption.value}.items`,
                                      ids: documentationCategoriesIds,
                                    }}
                                    formItemClassName={
                                      styles.accessListItemFieldEmpty
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })}
            </div>
          );
        })}
      </div>
    );
  },
);

export default memo(TeamAccessField);
